import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Row,
  Label,
  Input,
  Nav,
  NavItem,
  NavLink,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik, Formik, Field, ErrorMessage } from "formik";

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import usePersistStoreData from "../../../Components/Hooks/usePersistStoreData";
import NomineeFormFields from "./NomineeFormFields.js";
import EditNomineeDetailsModal from "../../../Components/Modals/EditNomineeDetailsModal";
import DocumentActionCard from "../../../Components/Common/DocumentActionCard";
import CommonLoading from "../../../Components/Common/CommonLoading";
import {
  ProfileDetailThunk,
  add_NomineeThunk,
  reKyc_Esign_DocumentDownloadThunk,
  re_kyc_nomineeNotOptedThunk,
} from "../../../slices/thunks";
import { showToast } from "../../../Components/ToastNotification";
import { formatDateToday } from "../../../Components/Common/WorkingDateRange";

const AddNominee = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { userData } = usePersistStoreData();
  const token = userData?.token;
  const [addNominee, setAddNominee] = useState("");
  const [loading, setLoading] = useState(false);
  const { addNomineeData, addNomineeLoading } = useSelector(
    (state) => state.Add_Nominee_Detail
  );
  const { reKyc_Esign_DocumentLoading } = useSelector(
    (state) => state.Re_Kyc_Esign_Document_Download
  );
  const { re_kyc_nomineeNotOptedLoading } = useSelector(
    (state) => state.Re_kyc_nomineeNotOpted
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  //states and logic for add nominee

  const FILE_SIZE = 2 * 1024 * 1024; // 2MB
  const SUPPORTED_FORMATS = ["image/png", "image/jpg", "image/jpeg"];

  const nomineeValidationSchema = Yup.object({
    nominee_name: Yup.string().trim().required("Nominee name is required"),

    nominee_share: Yup.number()
      .typeError("Share % must be a number")
      .min(1, "Minimum 1% required")
      .max(100, "Cannot exceed 100%")
      .required("Share % is required"),

    nominee_relationship: Yup.string().required("Relationship is required!"),

    nominee_email: Yup.string()
      .trim()
      .email("Enter a valid email")
      .required("Email is required"),

    nominee_mobile: Yup.string()
      .matches(/^[6-9]\d{9}$/, "Enter valid 10-digit mobile number")
      .required("Mobile number is required"),

    nominee_address: Yup.string().trim().required("Address is required"),

    nominee_identity: Yup.string()
      .matches(/^\d{4}$/, "Identity No must be exactly 4 digits")
      .required("Identity No is required"),

    nominee_document: Yup.mixed()
      .required("Nominee document is required")
      .test("fileType", "Only JPG, JPEG, PNG files are allowed", (value) => {
        return value && SUPPORTED_FORMATS.includes(value.type);
      })
      .test("fileSize", "File size must be less than 2 MB", (value) => {
        return value && value.size <= FILE_SIZE;
      }),
    is_minor: Yup.string(),
    nominee_gaurdian: Yup.string().when("is_minor", {
      is: "1",
      then: (schema) => schema.required("Guardian name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    nominee_dob: Yup.string().when("is_minor", {
      is: "1",
      then: (schema) => schema.required("DOB is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const validationAddNominee = useFormik({
    enableReinitialize: true, // Ensures Formik updates when initialValues change
    initialValues: {
      nominee_name: "",
      nominee_share: "",
      nominee_relationship: "",
      nominee_email: "",
      nominee_mobile: "",
      nominee_address: "",
      nominee_identity: "",
      nominee_document: null,
      nominee_gaurdian: "",
      nominee_dob: "",
      is_minor: "0",
    },
    validationSchema: nomineeValidationSchema,
    onSubmit: (values) => {
      // console.log("Form Submitted", values);
    },
  });

  // states and logic for add nominee table
  const [nomineeList, setNomineeList] = useState([]);

  const handleAddNominee = async () => {
    const isValid = await validationAddNominee.validateForm();
    validationAddNominee.setTouched({
      nominee_name: true,
      nominee_share: true,
      nominee_relationship: true,
      nominee_email: true,
      nominee_mobile: true,
      nominee_address: true,
      nominee_identity: true,
      nominee_document: true,
      nominee_dob: true,
      nominee_gaurdian: true,
    });

    if (Object.keys(isValid).length === 0) {
      if (nomineeList.length >= 10) {
        showToast("You can add a maximum of 10 nominees.");
        return;
      }

      setNomineeList([...nomineeList, validationAddNominee.values]);
      validationAddNominee.resetForm();
      validationAddNominee.setFieldValue("nominee_document", null, false);
      // clear the native file input UI
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Open Digio popup with API response data
  const openDigioPopup = (digioData) => {
    if (!window.Digio) {
      // console.error("Digio SDK not loaded");
      showToast(
        "Digio SDK is not loaded. Please refresh the page and try again.",
        "error"
      );
      setIsProcessing(false);
      return;
    }
    try {
      // Digio configuration with callback
      const options = {
        environment: "production", // or "sandbox" for testing
        callback: function (response) {
          if (response.hasOwnProperty("error_code")) {
            // console.log("Error occurred in process:", response);
            // Handle error
            setIsProcessing(false);
            showToast(response, "error");
            return;
          }
          showToast("Signing completed successfully", "success");
          // console.log("Signing completed successfully:", response);
          dispatch(
            reKyc_Esign_DocumentDownloadThunk({
              params: {
                type: addNominee == "0" ? "nonominee_opted" : "nominee_opted",
              },
              token,
            })
          )
            .unwrap()
            .then((result) => {
              const blob = new Blob([result], { type: "application/pdf" });

              const fileURL = window.URL.createObjectURL(blob);

              // Open in new tab
              window.open(fileURL, "_blank");

              // Also download
              const link = document.createElement("a");
              link.href = fileURL;
              link.download = "nominee_document.pdf"; // file name
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              // showToast("Downloaded successfully", "success");
              validationAddNominee.resetForm();
              navigate("/profile");
              setIsProcessing(false);
            })
            .catch((error) => {
              showToast(
                error?.message ||
                  "Something went wrong. Please try again later",
                "error"
              );
              setIsProcessing(false);
            })

            .finally(() => {
              setIsProcessing(false);
            });
        },
        event_listener: function () {
          // Prevent Digio SDK from throwing when trying to call this
          // console.log("Digio event listener triggered");
          setIsProcessing(false);
        },
      };

      // Create new Digio instance
      const digio = new window.Digio(options);

      // Initialize Digio
      digio.init();
      digio.submit(digioData?.id, digioData?.identifier);
    } catch (error) {
      console.error("Error opening Digio popup:", error);
      showToast("Error opening Digio: " + error.message, "error");
      setIsProcessing(false);
    }
  };

  const handleSubmitNominee = () => {
    // total nominee should less then 10
    if (nomineeList?.length > 10) {
      showToast("You can add a maximum of 10 nominees.");
      return;
    }
    // Check nominee share total equals exactly 100
    const totalShare = nomineeList.reduce((sum, nominee) => {
      const share = parseFloat(nominee.nominee_share);
      return sum + (isNaN(share) ? 0 : share);
    }, 0);

    if (totalShare !== 100) {
      showToast("Total nominee share must be exactly 100%.", "error");
      return;
    }

    const form = new FormData();

    nomineeList.forEach((nominee, index) => {
      const i = index + 1; // Start from 1 as per your example

      form.append(
        `nominee_details[${i}][nominee_identity]`,
        nominee.nominee_identity || ""
      );
      form.append(
        `nominee_details[${i}][nominee_name]`,
        nominee.nominee_name || ""
      );
      form.append(
        `nominee_details[${i}][nominee_email]`,
        nominee.nominee_email || ""
      );
      form.append(
        `nominee_details[${i}][nominee_relationship]`,
        nominee.nominee_relationship || ""
      );
      form.append(
        `nominee_details[${i}][nominee_mobile]`,
        nominee.nominee_mobile || ""
      );
      form.append(
        `nominee_details[${i}][nominee_address]`,
        nominee.nominee_address || ""
      );
      form.append(
        `nominee_details[${i}][is_nominee_minor]`,
        nominee.is_minor || "0"
      );
      if (nominee.is_minor == "1") {
        form.append(
          `nominee_details[${i}][nominee_dob]`,
          formatDateToday(nominee.nominee_dob) || ""
        );
        form.append(
          `nominee_details[${i}][nominee_gaurdian]`,
          nominee.nominee_gaurdian || ""
        );
      }

      form.append(
        `nominee_details[${i}][nominee_share]`,
        nominee.nominee_share || ""
      );

      if (nominee.nominee_document) {
        form.append(
          `nominee_details[${i}][nominee_document]`,
          nominee.nominee_document
        );
      }
    });

    dispatch(add_NomineeThunk({ body: form, token }))
      .then((res) => {
        if (res?.payload?.code == 200) {
          // showToast(res?.payload?.message, "success");
          openDigioPopup(res?.payload?.data);
        } else {
          showToast(res?.error?.message, "error");
        }
      })
      .catch((err) => {
        showToast(err, "error");
      });
  };

  const handleNotOptedNominee = () => {
    dispatch(re_kyc_nomineeNotOptedThunk({ token }))
      .then((res) => {
        if (res?.payload?.code == 200) {
          // showToast(res?.payload?.message, "success");
          openDigioPopup(res?.payload?.data);
        } else {
          showToast(res?.error?.message, "error");
        }
      })
      .catch((err) => {
        showToast(err, "error");
      });
  };

  const columns = [
    { key: "index", label: "S.No." },
    { key: "nominee_name", label: "Name" },
    { key: "nominee_share", label: "Share (%)" },
    { key: "nominee_relationship", label: "Relationship" },
    { key: "nominee_email", label: "Email" },
    { key: "nominee_mobile", label: "Mobile" },
    { key: "nominee_address", label: "Address" },
    { key: "nominee_identity", label: "Identity No" },
    { key: "nominee_document", label: "Document Name" },
    { key: "nominee_gaurdian", label: "Gaurdian" },
    { key: "nominee_dob", label: "DOB" },
    { key: "action", label: "Action" },
  ];

  const [editNomineeData, setEditNomineeData] = useState(null);
  const [editIndex, setEditIndex] = useState(null);

  const handleEditNominee = (index) => {
    setEdit_modal(true);
    setEditIndex(index);
    setEditNomineeData(nomineeList[index]);
  };
  // state for modal to edit nominee details
  const [editModal, setEdit_modal] = useState(false);
  const closeEditModal = () => {
    setEdit_modal(false);
  };

  // useEffect(() => {
  //   setLoading(true);
  //   dispatch(ProfileDetailThunk(userData?.token))
  //     .then((res) => {
  //       setLoading(false);
  //     })
  //     .catch((err) => {
  //       setLoading(false);
  //     });
  // }, [userData?.token]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {loading ? (
            <div
              className="centerContent h-100 w-100"
              style={{ minHeight: "80vh" }}
            >
              <CommonLoading />
            </div>
          ) : (
            <>
              <div className="position-relative mx-n4 mt-n4">
                <div
                  className="profile-wid-bg profile-setting-img"
                  style={{ backgroundColor: "#01256D" }}
                >
                  <div className="overlay-content">
                    <div className="text-end p-3">
                      <div className="p-0 ps-2 ms-auto rounded-circle profile-photo-edit">
                        <Nav
                          pills
                          className=" animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1"
                          role="tablist"
                        >
                          <NavItem>
                            <NavLink
                              // className={classnames(" ", {
                              //   active: activeTab === "1",
                              // })}
                              onClick={() => {
                                navigate("/edit_option");
                              }}
                              style={{
                                backgroundColor: "transparent",
                                border: "1px solid #0FA67B",
                              }}
                            >
                              <svg
                                className="me-2"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3.99 9L16 9L16 7L3.99 7L3.99 4L3.49691e-07 8L3.99 12L3.99 9Z"
                                  fill="#0FA67B"
                                />
                              </svg>
                              <span className="d-none d-md-inline-block">
                                Go Back
                              </span>
                            </NavLink>
                          </NavItem>
                        </Nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Row>
                <Col xxl={12} xl={12}>
                  <Card className="mt-n5">
                    <CardHeader>
                      <h5 className="card-title mb-0">Add Nominee</h5>
                    </CardHeader>
                    <CardBody>
                      <Row className="row m-0 p-0">
                        <Col lg={12} className="mb-3 ps-0">
                          <div className="fs-16 fw-semibold mt-0">
                            Nominee Details
                          </div>
                          <div className="text-dark fs-14 fw-medium">
                            {userData?.personal_details?.nominee_full_name ??
                              "-"}
                          </div>
                        </Col>
                        <Col lg={6} className="mb-3 ps-0">
                          <Label className="form-label" htmlFor="add_nominee">
                            Do you want to add Nominee ?
                          </Label>

                          <div id="add_nominee">
                            <FormGroup check inline className="">
                              <Input
                                type="radio"
                                name="add_nominee"
                                id="otherYes"
                                value="1"
                                checked={addNominee === "1"}
                                onChange={(e) => setAddNominee(e.target.value)}
                              />
                              <Label check htmlFor="otherYes">
                                Yes
                              </Label>
                            </FormGroup>
                            <FormGroup check inline className="ms-3">
                              <Input
                                type="radio"
                                name="add_nominee"
                                id="otherNo"
                                value="0"
                                checked={addNominee === "0"}
                                onChange={(e) => setAddNominee(e.target.value)}
                              />
                              <Label check htmlFor="otherNo">
                                No
                              </Label>
                            </FormGroup>
                          </div>
                        </Col>

                        {addNominee === "1" ? (
                          <>
                            <NomineeFormFields
                              formik={validationAddNominee}
                              nomineeList={nomineeList}
                              handleAddNominee={handleAddNominee}
                              columns={columns}
                              handleEditNominee={handleEditNominee}
                              setNomineeList={setNomineeList}
                              editIndex={editIndex}
                              setEditIndex={setEditIndex}
                              fileInputRef={fileInputRef}
                            />
                          </>
                        ) : addNominee === "0" ? (
                          <DocumentActionCard
                            text="Nominee not opted document"
                            iconType="download"
                            // onClick={() => {
                            //   handleDownloadDocument();
                            // }}
                            supportText="You have not opted the nominee, 
                                    so please download the document, sign it 
                                    manually, and submit it to the head office."
                          />
                        ) : null}

                        {addNominee === "1" && nomineeList?.length > 0 && (
                          <div className="d-flex align-items-center justify-content-end">
                            <Button
                              type="button"
                              color="primary"
                              onClick={handleSubmitNominee}
                              disabled={
                                isProcessing ||
                                addNomineeLoading ||
                                reKyc_Esign_DocumentLoading
                              }
                              style={{
                                minWidth: "120px",
                              }}
                            >
                              {isProcessing ||
                              addNomineeLoading ||
                              reKyc_Esign_DocumentLoading ? (
                                <Spinner size="sm" />
                              ) : (
                                "Proceed"
                              )}
                            </Button>
                          </div>
                        )}
                        {addNominee === "0" && (
                          <div className="d-flex align-items-center justify-content-end">
                            <Button
                              type="button"
                              color="primary"
                              onClick={handleNotOptedNominee}
                              disabled={
                                isProcessing ||
                                re_kyc_nomineeNotOptedLoading ||
                                reKyc_Esign_DocumentLoading
                              }
                              style={{
                                minWidth: "160px",
                              }}
                            >
                              {isProcessing ||
                              re_kyc_nomineeNotOptedLoading ||
                              reKyc_Esign_DocumentLoading ? (
                                <Spinner size="sm" />
                              ) : (
                                "Proceed"
                              )}
                            </Button>
                          </div>
                        )}
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>

      {editModal && (
        <EditNomineeDetailsModal
          editModal={editModal}
          closeEditModal={closeEditModal}
          editNomineeData={editNomineeData}
          editIndex={editIndex}
          nomineeList={nomineeList}
          setNomineeList={setNomineeList}
          validationAddNominee={nomineeValidationSchema}
        />
      )}
    </React.Fragment>
  );
};

export default AddNominee;
